/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, ThemeProvider, Typography } from '@mui/material';
import type { FC, Ref } from 'react';
import { CustomIconButton, dateUtils } from '@opengeoweb/shared';
import { ChevronRight, darkTheme, Options } from '@opengeoweb/theme';
import { dateToManualFormatString } from './clockContainerUtils';
import { useWebmapReactTranslation } from '../../utils/i18n';

const getButtonSizes = (
  fontSize: number,
): { buttonSize: 'small' | 'medium' | 'large'; iconSize: number } => {
  if (fontSize > 40) {
    return { buttonSize: 'large', iconSize: 40 };
  }
  if (fontSize > 24) {
    return { buttonSize: 'medium', iconSize: 32 };
  }
  return { buttonSize: 'small', iconSize: 24 };
};

export const ClockContainer: FC<{
  isOptionsMenuVisible?: boolean;
  onToggleClock: () => void;
  onToggleOptions: () => void;
  optionsElement: Ref<HTMLButtonElement>;
  fontSize: number;
  time: number;
}> = ({
  isOptionsMenuVisible = false,
  fontSize,
  onToggleClock,
  time,
  onToggleOptions,
  optionsElement,
}) => {
  const { t } = useWebmapReactTranslation();
  const title = t('clock-hide');
  const optionsTitle = t('clock-animation-options');
  const currentDateString = dateToManualFormatString(dateUtils.fromUnix(time));
  const { buttonSize, iconSize } = getButtonSizes(fontSize);
  return (
    <ThemeProvider theme={darkTheme}>
      <Grid
        container
        sx={{
          justifyContent: 'space-evenly',
          alignItems: 'center',
          cursor: 'move',
          height: '100%',
          width: '100%',
          backgroundColor: 'geowebColors.timeSlider.playerTimeMarkers.fill',
          borderRadius: '5px',
        }}
        wrap="nowrap"
      >
        <Grid sx={{ marginLeft: '12px', marginRight: '12px' }}>
          <CustomIconButton
            aria-label={title}
            onClick={onToggleClock}
            size={buttonSize}
            tooltipTitle={title}
            variant="boxed"
          >
            <ChevronRight sx={{ width: iconSize, height: iconSize }} />
          </CustomIconButton>
        </Grid>
        <Grid>
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              fontSize,
              color: 'geowebColors.timeSlider.playerTimeText.color',
            }}
            variant="subtitle2"
          >
            {currentDateString}
          </Typography>
        </Grid>
        <Grid sx={{ marginLeft: '12px', marginRight: '12px' }}>
          <CustomIconButton
            aria-label={optionsTitle}
            isSelected={isOptionsMenuVisible}
            onClick={onToggleOptions}
            ref={optionsElement}
            size={buttonSize}
            tooltipTitle={optionsTitle}
            variant="boxed"
          >
            <Options sx={{ width: iconSize, height: iconSize }} />
          </CustomIconButton>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};
